import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { isLocalStorageAvailable, storage } from '../../utils/storage';

import { Country } from '../../generated/graphql';
import { config } from '../../config';
import { session } from '../../utils/auth';

type SetSelectedType<T> = (value?: T) => void;

export const CountryContext = createContext({
  countrySelected: undefined as (Country | undefined),
  setCountrySelected: (() => null) as SetSelectedType<Country | undefined>,
});

export const useCountryContext = () => {
  const { countrySelected,
    setCountrySelected: setContextCountrySelected } = useContext(CountryContext);

  const setCountrySelected = useCallback(
    (country: Country | undefined) => {
      setContextCountrySelected(country);
    }, [ setContextCountrySelected ],
  );

  return [ countrySelected, setCountrySelected ] as const;
};

export const CountryContextProvider: React.FC = ({ children }) => {
  const [ country, setCountry ] = useState<Country | undefined>(undefined);

  const { search } = useLocation();

  const queryCountry: Country | undefined = useMemo(() => {
    const query = new URLSearchParams(search);
    const countryString = query.get('country')?.toUpperCase();
    return countryString as Country;
  }, [ search ]);

  const memoizedCountry = useMemo(() => {
    if (country) return country;
    const storageCountry = isLocalStorageAvailable()
      ? storage.getItem(config.countryKey) : undefined;
    if (storageCountry) return storageCountry as Country | undefined;
    return undefined;
  }, [ country ]) || session.country;

  const countrySelected = session.country || memoizedCountry;

  const setCountrySelected = useCallback((countryValue: Country | undefined) => {
    if (countryValue && isLocalStorageAvailable()) storage.setItem(config.countryKey, countryValue);
    setCountry(countryValue);
  }, [ setCountry ]);

  useEffect(() => {
    if (!queryCountry) return;
    setCountrySelected(queryCountry);
  }, [ setCountrySelected, queryCountry ]);

  return (
    <CountryContext.Provider value={{ countrySelected, setCountrySelected }}>
      {children}
    </CountryContext.Provider>
  );
};
